import { z } from 'zod';

import { Nullable } from '@clearverify/types';

import {
  QueryMode,
  UserStatus,
  VerifierInfoFragment,
  VerifierStaffRole,
  VerifierWhere,
} from 'src/api';
import { COMPANY_SEARCH_CATEGORY_MAP } from 'src/constants/company';
import { getEmailDomainForSearch } from 'src/utils/email';

import {
  MANUAL_VERIFICATION_DISABLED_CREDENTIAL_STATUSES,
  MIDDESK_VERIFICATION_DISABLED_CREDENTIAL_STATUSES,
} from './constants';

export const isMiddeskVerificationDisabled = (verifier: VerifierInfoFragment): boolean => {
  return (
    MIDDESK_VERIFICATION_DISABLED_CREDENTIAL_STATUSES.includes(verifier.credentialStatus) ||
    !verifier.verifierStaffsList.some(staff => staff.user.status === UserStatus.Active)
  );
};

export const isManualVerificationDisabled = (
  verifier: VerifierInfoFragment,
  isMiddeskEnabled = false,
): boolean => {
  const isAnyActiveVerifierAdmin = verifier.verifierStaffsList.some(verifierStaff => {
    return (
      verifierStaff.user.status === UserStatus.Active &&
      verifierStaff.role === VerifierStaffRole.VerifierAdmin
    );
  });

  if (!isAnyActiveVerifierAdmin) {
    return true;
  }

  return isMiddeskEnabled
    ? MANUAL_VERIFICATION_DISABLED_CREDENTIAL_STATUSES.includes(verifier.credentialStatus)
    : false;
};

export const prepareSearchFilterByCategories = (
  searchValue: Nullable<string>,
  searchCategory: Nullable<string>,
): VerifierWhere => {
  if (!searchValue) return {};
  // eslint-disable-next-line no-param-reassign
  if (!searchCategory) searchCategory = COMPANY_SEARCH_CATEGORY_MAP.displayName;

  if (searchCategory === COMPANY_SEARCH_CATEGORY_MAP.displayName) {
    return {
      displayName: {
        contains: searchValue,
        mode: QueryMode.Insensitive,
      },
    };
  }

  if (searchCategory === COMPANY_SEARCH_CATEGORY_MAP.staffEmail) {
    const { success, data: staffEmail } = z.string().trim().email().safeParse(searchValue);
    if (!success || !staffEmail) return {};

    return {
      verifierStaffs: {
        some: {
          user: {
            email: { equals: staffEmail.toLowerCase() },
          },
        },
      },
    };
  }

  if (searchCategory === COMPANY_SEARCH_CATEGORY_MAP.emailDomain) {
    const emailDomain = getEmailDomainForSearch(searchValue);
    if (!emailDomain) return {};

    return {
      OR: [
        { contactEmail: { endsWith: emailDomain.toLowerCase() } },
        {
          verifierStaffs: {
            some: {
              user: { email: { endsWith: emailDomain.toLowerCase() } },
            },
          },
        },
      ],
    };
  }

  return {};
};
